import { useTranslation } from 'react-i18next';
import Skeleton from '@/components/base/Skeleton';
import { formatCurrency } from '@/lib/format';
import type { FinanceSummary } from '@/types';

interface RevenueCardsProps {
  summary: FinanceSummary | null;
  loading: boolean;
}

const TONES = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-600',
  emerald: 'bg-emerald-50 text-emerald-600',
};

export default function RevenueCards({ summary, loading }: RevenueCardsProps) {
  const { t } = useTranslation();

  if (loading || !summary) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-lg border border-background-200 bg-background-50 shadow-xs p-4 md:p-5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="mt-3 h-8 w-24" />
            <Skeleton className="mt-2 h-3 w-40" />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      key: 'direct',
      label: t('finance.card.direct'),
      hint: t('finance.card.directHint'),
      value: formatCurrency(summary.directRevenue, summary.currency),
      icon: 'ri-shopping-bag-3-line',
      tone: TONES.primary,
    },
    {
      key: 'deliveryFees',
      label: t('finance.card.deliveryFees'),
      hint: t('finance.card.deliveryFeesHint'),
      value: formatCurrency(summary.transferredDeliveryFees, summary.currency),
      icon: 'ri-e-bike-2-line',
      tone: TONES.accent,
    },
    {
      key: 'total',
      label: t('finance.card.total'),
      hint: t('finance.card.totalHint'),
      value: formatCurrency(summary.totalRevenue, summary.currency),
      icon: 'ri-wallet-3-line',
      tone: TONES.emerald,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {cards.map((card) => (
        <div
          key={card.key}
          className="rounded-lg border border-background-200 bg-background-50 shadow-xs p-4 md:p-5 animate-fade-in-up"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-foreground-500">{card.label}</p>
              <p className="mt-2 text-2xl font-bold text-foreground-950">{card.value}</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-400">{card.hint}</p>
            </div>
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${card.tone}`}>
              <i className={`${card.icon} text-xl leading-none`} aria-hidden="true" />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}